import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Calendar, Clock, CreditCard, Trash2, AlertCircle, Edit2, Save } from 'lucide-react';
import { updateDoc, doc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import AlertModal from './AlertModal.js';
import { useAlert } from '../hooks/UseAlert.js';

const BookingDetailModal = ({ booking, onClose, onDelete, onUpdate, isFullAdmin }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editedBooking, setEditedBooking] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);

  const { alertConfig, closeAlert, error, success, warning } = useAlert();

  // Horaires d'ouverture
  const openingHours = {
    1: null,
    2: { start: "14:00", end: "20:00" },
    3: { start: "11:00", end: "20:00" },
    4: { start: "11:00", end: "20:00" },
    5: { start: "11:00", end: "20:00" },
    6: { start: "10:00", end: "18:00" },
    0: null,
  };

  useEffect(() => {
    if (booking) {
      setEditedBooking({
        ...booking,
        date: booking.date,
        timeSlot: booking.timeSlot,
        serviceName: booking.serviceName,
        serviceDuration: booking.serviceDuration,
        servicePrice: booking.servicePrice,
        clientName: booking.clientName || '',
      });
    }
  }, [booking]);

  useEffect(() => {
    if (isEditing && editedBooking?.date) {
      fetchBlockedSlots(editedBooking.date);
    }
  }, [isEditing, editedBooking?.date]);

  if (!booking || !editedBooking) return null;

  const extractAmount = (priceStr) => {
    const match = priceStr.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const depositAmount = Math.round(extractAmount(booking.servicePrice) * 0.20 * 100) / 100;
  const remainingAmount = (extractAmount(booking.servicePrice) - depositAmount).toFixed(2);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'confirmed': { label: 'Confirmée', color: 'bg-green-100 text-green-800' },
      'pending_payment': { label: 'En attente de paiement', color: 'bg-amber-100 text-amber-800' },
      'cancelled': { label: 'Annulée', color: 'bg-red-100 text-red-800' },
      'blocked': { label: 'Bloquée (Admin)', color: 'bg-purple-100 text-purple-800' }
    };
    
    const badge = badges[status] || badges.confirmed;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const badges = {
      'authorized': { label: 'Autorisé (à capturer)', color: 'bg-blue-100 text-blue-800' },
      'captured': { label: 'Encaissé', color: 'bg-green-100 text-green-800' },
      'pending': { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
      'cancelled': { label: 'Annulé', color: 'bg-red-100 text-red-800' },
      'refunded': { label: 'Remboursé', color: 'bg-purple-100 text-purple-800' },
      'pay_on_site': { label: 'Paiement sur place', color: 'bg-gray-100 text-gray-800' }
    };
    
    const badge = badges[paymentStatus] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  // Récupérer les créneaux bloqués pour la date sélectionnée
  const fetchBlockedSlots = async (date) => {
    try {
      const bookingsRef = collection(db, 'bookings');
      const q = query(bookingsRef, where('date', '==', date));
      const querySnapshot = await getDocs(q);

      const allBlockedSlots = [];
      querySnapshot.forEach((doc) => {
        const bookingData = doc.data();
        // Ne pas inclure les créneaux de la réservation actuelle
        if (doc.id !== booking.id) {
          allBlockedSlots.push(...bookingData.blockedSlots);
        }
      });

      setBlockedSlots(allBlockedSlots);
      generateAvailableTimeSlots(date, allBlockedSlots);
    } catch (error) {
      console.error('Erreur chargement créneaux:', error);
      setBlockedSlots([]);
    }
  };

  // Générer les créneaux disponibles
  const generateAvailableTimeSlots = (date, blocked) => {
    const dayOfWeek = new Date(date).getDay();
    const hours = openingHours[dayOfWeek];

    if (!hours) {
      setAvailableTimeSlots([]);
      return;
    }

    const slots = [];
    const [startH, startM] = hours.start.split(':').map(Number);
    const [endH, endM] = hours.end.split(':').map(Number);
    
    let currentMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    while (currentMinutes <= endMinutes) {
      const h = Math.floor(currentMinutes / 60);
      const m = currentMinutes % 60;
      const timeSlot = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      
      // Ne garder que les créneaux non bloqués
      if (!blocked.includes(timeSlot)) {
        slots.push(timeSlot);
      }
      
      currentMinutes += 15;
    }

    setAvailableTimeSlots(slots);
  };

  // Calculer les nouveaux créneaux bloqués
  const calculateBlockedSlots = (startTime, duration) => {
    const slots = [];
    const [hours, minutes] = startTime.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;
    
    let durationMinutes = 0;
    if (duration.includes('h')) {
      const parts = duration.match(/(\d+)h(\d+)?/);
      if (parts) {
        durationMinutes = parseInt(parts[1]) * 60 + (parts[2] ? parseInt(parts[2]) : 0);
      }
    } else if (duration.includes('mins')) {
      durationMinutes = parseInt(duration);
    }
    
    // Ajouter 15 minutes de nettoyage
    const totalDuration = durationMinutes + 15;
    const slotsNeeded = Math.ceil(totalDuration / 15);

    for (let i = 0; i < slotsNeeded; i++) {
      const slotMinutes = totalMinutes + (i * 15);
      const h = Math.floor(slotMinutes / 60);
      const m = slotMinutes % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    
    return slots;
  };

  // Activer le mode édition
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Annuler les modifications
  const handleCancelEdit = () => {
    setEditedBooking({
      ...booking,
      date: booking.date,
      timeSlot: booking.timeSlot,
      serviceName: booking.serviceName,
      serviceDuration: booking.serviceDuration,
      servicePrice: booking.servicePrice,
      clientName: booking.clientName || '',
    });
    setIsEditing(false);
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    if (!editedBooking.date || !editedBooking.timeSlot) {
      error('Veuillez sélectionner une date et un créneau');
      return;
    }

    setLoading(true);

    try {
      // Calculer les nouveaux créneaux bloqués
      const newBlockedSlots = calculateBlockedSlots(
        editedBooking.timeSlot,
        editedBooking.serviceDuration
      );

      // Vérifier les conflits (en excluant la réservation actuelle)
      await fetchBlockedSlots(editedBooking.date);
      const hasConflict = newBlockedSlots.some(slot => blockedSlots.includes(slot));

      if (hasConflict) {
        error('Conflit de créneaux ! Ce créneau est déjà réservé.');
        setLoading(false);
        return;
      }

      // Mettre à jour dans Firestore
      const bookingRef = doc(db, 'bookings', booking.id);
      await updateDoc(bookingRef, {
        date: editedBooking.date,
        timeSlot: editedBooking.timeSlot,
        serviceName: editedBooking.serviceName,
        serviceDuration: editedBooking.serviceDuration,
        servicePrice: editedBooking.servicePrice,
        blockedSlots: newBlockedSlots,
        clientName: editedBooking.clientName || booking.clientName,
        updatedAt: new Date().toISOString()
      });

      success('Réservation modifiée avec succès !');
      
      // Appeler le callback pour rafraîchir le calendrier
      if (onUpdate) {
        onUpdate();
      }

      setIsEditing(false);
      onClose();

    } catch (error) {
      console.error('Erreur modification:', error);
      error('Erreur lors de la modification. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
        {/* En-tête */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-light text-purple-900 mb-2">
              {isEditing ? 'Modifier la réservation' : 'Détails de la réservation'}
            </h2>
            <div className="flex gap-2">
              {getStatusBadge(booking.status)}
              {booking.paymentStatus && getPaymentStatusBadge(booking.paymentStatus)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Informations du soin */}
        <div className="bg-purple-50 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Informations du soin
          </h3>
          <div className="space-y-3">
            {/* Prestation */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Prestation :</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedBooking.serviceName}
                  onChange={(e) => setEditedBooking({ ...editedBooking, serviceName: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none max-w-xs"
                />
              ) : (
                <span className="font-semibold text-purple-900">{booking.serviceName}</span>
              )}
            </div>

            {/* Date */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Date :</span>
              {isEditing ? (
                <input
                  type="date"
                  value={editedBooking.date}
                  onChange={(e) => setEditedBooking({ ...editedBooking, date: e.target.value, timeSlot: '' })}
                  min={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              ) : (
                <span className="font-medium text-gray-900">{formatDate(booking.date)}</span>
              )}
            </div>

            {/* Heure */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Heure :</span>
              {isEditing ? (
                <select
                  value={editedBooking.timeSlot}
                  onChange={(e) => setEditedBooking({ ...editedBooking, timeSlot: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                  disabled={!editedBooking.date}
                >
                  <option value="">Sélectionner un créneau</option>
                  {availableTimeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="font-medium text-gray-900">{booking.timeSlot}</span>
              )}
            </div>

            {/* Durée */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Durée :</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedBooking.serviceDuration}
                  onChange={(e) => setEditedBooking({ ...editedBooking, serviceDuration: e.target.value })}
                  placeholder="Ex: 1h00, 1h30, 45mins"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none w-32"
                />
              ) : (
                <span className="font-medium text-gray-900">{booking.serviceDuration}</span>
              )}
            </div>

            {/* Prix */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Prix :</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedBooking.servicePrice}
                  onChange={(e) => setEditedBooking({ ...editedBooking, servicePrice: e.target.value })}
                  placeholder="Ex: 90€"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none w-24"
                />
              ) : (
                <span className="font-bold text-purple-700 text-lg">{booking.servicePrice}</span>
              )}
            </div>

            {/* Nom client (pour blocages admin) */}
            {(booking.isAdminBlock || isEditing) && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Client :</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedBooking.clientName}
                    onChange={(e) => setEditedBooking({ ...editedBooking, clientName: e.target.value })}
                    placeholder="Nom du client"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none max-w-xs"
                  />
                ) : booking.clientName ? (
                  <span className="font-medium text-purple-700 italic">{booking.clientName}</span>
                ) : (
                  <span className="text-gray-400 italic">Non renseigné</span>
                )}
              </div>
            )}

            {/* Email du client (blocages admin uniquement) */}
            {booking.isAdminBlock && booking.clientEmail && !isEditing && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Email client :</span>
                <a 
                  href={`mailto:${booking.clientEmail}`}
                  className="font-medium text-purple-600 hover:underline"
                >
                  {booking.clientEmail}
                </a>
              </div>
            )}

            {/* Téléphone du client (blocages admin uniquement) */}
            {booking.isAdminBlock && booking.clientPhone && !isEditing && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Téléphone client :</span>
                <a 
                  href={`tel:${booking.clientPhone}`}
                  className="font-medium text-purple-600 hover:underline"
                >
                  {booking.clientPhone}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Informations client - Visible uniquement pour l'admin complet (mode lecture) */}
        {!isEditing && isFullAdmin && !booking.isAdminBlock && (
          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Informations client
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Nom</p>
                  <p className="font-medium text-gray-900">{booking.userName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Email</p>
                  <a 
                    href={`mailto:${booking.userEmail}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {booking.userEmail}
                  </a>
                </div>
              </div>
              {booking.userPhone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Téléphone</p>
                    <a 
                      href={`tel:${booking.userPhone}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {booking.userPhone}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Informations de paiement (mode lecture uniquement) */}
        {!isEditing && booking.paymentIntentId && (
          <div className="bg-green-50 p-6 rounded-lg mb-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Informations de paiement
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Type de paiement :</span>
                {booking.paymentType === 'full' ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    Paiement complet
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    Accompte (20%)
                  </span>
                )}
              </div>

              <div className="flex justify-between">
                <span className="text-gray-700">
                  {booking.paymentType === 'full' ? 'Montant total payé :' : 'Accompte payé (20%) :'}
                </span>
                <span className="font-bold text-green-700">
                  {booking.amountPaid || depositAmount}€
                </span>
              </div>

              {booking.paymentType === 'deposit' && (
                <div className="flex justify-between">
                  <span className="text-gray-700">Restant à régler sur place :</span>
                  <span className="font-semibold text-gray-900">
                    {(extractAmount(booking.servicePrice) - (booking.amountPaid || depositAmount)).toFixed(2)}€
                  </span>
                </div>
              )}

              {booking.paymentType === 'full' && (
                <div className="flex items-center gap-2 text-green-700 bg-green-100 p-2 rounded">
                  <span className="text-sm font-medium">✓ Prestation entièrement réglée</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-gray-700">Statut du paiement :</span>
                {getPaymentStatusBadge(booking.paymentStatus)}
              </div>

              {booking.paymentIntentId && (
                <div className="pt-2 border-t border-green-200">
                  <p className="text-xs text-gray-600">ID Transaction</p>
                  <p className="text-xs font-mono text-gray-500 break-all">{booking.paymentIntentId}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Informations techniques (mode lecture uniquement) */}
        {!isEditing && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Créée le :</p>
                <p className="font-medium text-gray-900">
                  {new Date(booking.createdAt).toLocaleString('fr-FR')}
                </p>
              </div>
              {booking.confirmedAt && (
                <div>
                  <p className="text-gray-600">Confirmée le :</p>
                  <p className="font-medium text-gray-900">
                    {new Date(booking.confirmedAt).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
            {booking.blockedSlots && booking.blockedSlots.length > 0 && (
              <div className="mt-3">
                <p className="text-gray-600 text-xs mb-1">Créneaux bloqués :</p>
                <div className="flex flex-wrap gap-1">
                  {booking.blockedSlots.map((slot, index) => (
                    <span key={index} className="text-xs bg-gray-200 px-2 py-1 rounded">
                      {slot}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Message d'aide en mode édition */}
        {isEditing && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
            <div className="flex gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">⚠️ Modification de réservation</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Les créneaux bloqués seront automatiquement recalculés</li>
                  <li>Les conflits avec d'autres réservations seront vérifiés</li>
                  <li>Les informations de paiement ne sont pas modifiables</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {isFullAdmin && (
          <div className="flex gap-4">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  disabled={loading}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className={`flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Save className="w-5 h-5" />
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={handleEdit}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-5 h-5" />
                  Modifier
                </button>
                {booking.status !== 'cancelled' && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette réservation ?\n\nClient : ${booking.userName}\nSoin : ${booking.serviceName}\nDate : ${formatDate(booking.date)}`)) {
                        onDelete(booking.id);
                      }
                    }}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    Supprimer
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
</>
  );
};

export default BookingDetailModal;